/**
 * =============================================================================
 * V1 / V2 authenticated routes — single module (wrappers + decision helpers)
 * =============================================================================
 *
 * 1) TOGGLEABLE MIGRATION (V1 + V2 exist) — V2ToggleRoute, V2RouteWrapper
 *    Rule: useV2 = getFlag(flagKey) && preferV2
 *    - Rollout: src/v2/core/flags.js (USE_V2_*)
 *    - User:    src/context/experienceToggleContext.jsx (preferV2)
 *
 * 2) V2 ONLY (no V1) — V2OnlyRoute
 *    Always AuthLayoutV2 + V2 page; ignores toggle and migration flags.
 *
 * 3) V1 ONLY — plain <RequireAuth> (no wrapper here)
 *
 * 4) PUBLIC TOGGLE — PublicV2ToggleRoute (login, etc.): no RequireAuth; flag + preferV2 only.
 *
 * | Wrapper        | V1? | Flag | Toggle | Notes                          |
 * |----------------|-----|------|--------|--------------------------------|
 * | V2ToggleRoute  | yes | yes  | yes    | v1ComponentProps / v2…        |
 * | V2RouteWrapper | yes | yes  | yes    | optional useV2Layout override  |
 * | V2OnlyRoute    | no  | no   | no     | v2ComponentProps only          |
 */

import React from "react";
import RequireAuth from "./RequireAuth";
import { getFlag } from "../v2/core/flags";
import { useV2Toggle } from "../context/experienceToggleContext";

// --- policy helpers (used only by this file) ---

function shouldUseV2ForToggleableFeature(flagKey, preferV2) {
  return getFlag(flagKey) && !!preferV2;
}

function resolveUseV2Layout(derivedUseV2, layoutOverride) {
  return layoutOverride !== undefined ? layoutOverride : derivedUseV2;
}

// --- wrappers ---

export function V2ToggleRoute({
  flag,
  V2Component,
  V1Component,
  v1ComponentProps = {},
  v2ComponentProps = {},
  ...requireAuthProps
}) {
  const { preferV2 } = useV2Toggle();
  const useV2 = shouldUseV2ForToggleableFeature(flag, preferV2);

  return (
    <RequireAuth key={useV2 ? "v2" : "v1"} useV2Layout={useV2} {...requireAuthProps}>
      {useV2 ? <V2Component {...v2ComponentProps} /> : <V1Component {...v1ComponentProps} />}
    </RequireAuth>
  );
}

export function V2RouteWrapper({
  flag,
  V2Component,
  V1Component,
  useV2Layout: useV2LayoutProp,
  ...requireAuthProps
}) {
  const { preferV2 } = useV2Toggle();
  const useV2 = shouldUseV2ForToggleableFeature(flag, preferV2);
  const useV2Layout = resolveUseV2Layout(useV2, useV2LayoutProp);

  return (
    <RequireAuth key={useV2 ? "v2" : "v1"} useV2Layout={useV2Layout} {...requireAuthProps}>
      {useV2 ? <V2Component /> : <V1Component />}
    </RequireAuth>
  );
}

export function V2OnlyRoute({ V2Component, v2ComponentProps = {}, ...requireAuthProps }) {
  return (
    <RequireAuth useV2Layout {...requireAuthProps}>
      <V2Component {...v2ComponentProps} />
    </RequireAuth>
  );
}

/** Unauthenticated routes (e.g. login): V1/V2 by flag + user toggle; no layout wrapper. */
export function PublicV2ToggleRoute({
  flag,
  V2Component,
  V1Component,
  v1ComponentProps = {},
  v2ComponentProps = {},
}) {
  const { preferV2 } = useV2Toggle();
  const useV2 = shouldUseV2ForToggleableFeature(flag, preferV2);
  return useV2 ? <V2Component {...v2ComponentProps} /> : <V1Component {...v1ComponentProps} />;
}
